import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ValidationType } from '../../../../types/ValidationType';
import { useInputValidator } from '../../../hooks/validationHooks/useInputValidator';
import { Flex } from '../../flex';
import {
  NavigationAndActionHide,
  NavigationAndActionSearch,
  NavigationAndActionShow,
} from '../../icons';
import { theme } from '../../theme';
import {
  IconsWrapper,
  InputError,
  IconContainer,
  InputWrapper,
  Prefix,
  StyledClearIcon,
  StyledInput,
  Sufix,
} from './styled';

export type InputFieldHandleType = {
  focus: () => void;
  clearValue: () => void;
  currentValue: () => string | undefined;
  changeInputFieldValue: (value: string) => void;
};

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  type?: string;
  inputChangeAction?: (value: string) => void;
  placeholderText?: string;
  isLoading?: boolean;
  timeoutOnInputChangeInMs?: number;
  loaderColor?: string;
  icon?: React.ReactNode;
  iconClickAction?: (value: boolean) => void;
  errorText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: string | number | readonly string[];
  className?: string;
  onFilledInput?: (arg: any) => void;
  validators?: ValidationType[];
  validate?: boolean;
  onClear?: () => void;
  parser?: (value: any) => string;
  maxLength?: number;
  prefix?: string;
  sufix?: string;
}

export const Input: React.FC<InputProps> = ({
  placeholderText,
  type = 'text',
  isLoading = false,
  timeoutOnInputChangeInMs = 100,
  loaderColor = theme.colors.primaryK10,
  icon,
  iconClickAction,
  errorText,
  defaultValue,
  className,
  onFilledInput,
  validate = false,
  validators,
  onKeyDown,
  onChange,
  onBlur,
  onFocus,
  onClear,
  parser,
  maxLength,
  prefix,
  sufix,
  ...props
}) => {
  const validateField = useInputValidator();
  const [value, setValue] = useState<typeof defaultValue>('');
  const [iconState, setIconState] = useState<boolean>(false);
  const [lastErrorText, setLastErrorText] = useState<string | undefined>(
    errorText,
  );
  const [showError, setShowError] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>();

  const [showClearIcon, setShowClearIcon] = useState(false);

  const ref = useRef<null | HTMLInputElement>(null);
  let timer: ReturnType<typeof setTimeout> | null = null;

  useEffect(() => {
    const valError = validators
      ? validateField(value as string, validators)
      : undefined;
    setValidationError(valError);

    let error = errorText;
    if (validate && valError) {
      error = valError;
    }
    setLastErrorText(error);
    setShowError(error !== undefined && error.length > 0);
  }, [
    errorText,
    lastErrorText,
    showError,
    validate,
    validators,
    value,
    validateField,
  ]);

  useEffect(() => {
    if (defaultValue !== undefined) {
      if (parser && type !== 'date' && type !== 'integer') {
        setValue((+defaultValue).toFixed(2).replace('.', ','));
      } else {
        setValue(defaultValue);
      }
    }
    setShowClearIcon(!!defaultValue);
  }, [defaultValue, parser, type]);

  const changeInputValue = (newValue: string) => {
    setValue(newValue);
    onFilledInput?.(newValue);

    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      if (props.inputChangeAction && ref?.current?.value === newValue) {
        props.inputChangeAction(newValue);
      }
    }, timeoutOnInputChangeInMs);

    setShowClearIcon(!!newValue);
  };

  const handleKeyDown = useCallback(
    (event: any) => {
      if (type === 'search' && event.key === 'Enter' && iconClickAction) {
        timer && clearTimeout(timer);
        iconClickAction(event);
      }
      onKeyDown && onKeyDown(event);
    },
    [iconClickAction, onKeyDown, type, timer],
  );

  const Icon = useMemo(() => {
    if (icon) return icon;
    if (type === 'password') {
      return iconState ? (
        <NavigationAndActionHide />
      ) : (
        <NavigationAndActionShow />
      );
    }
    if (type === 'search') {
      return <NavigationAndActionSearch />;
    }
    return undefined;
  }, [icon, type, iconState]);

  const handleIconClick = useCallback(() => {
    setIconState(!iconState);
    if (Icon && iconClickAction) {
      timer && clearTimeout(timer);
      iconClickAction(iconState);
    }
  }, [Icon, iconState, iconClickAction, timer]);

  const inputType = useMemo(() => {
    if (type === 'password') {
      return iconState ? 'text' : 'password';
    }
    if (type === 'date') {
      return 'text';
    }
    return type;
  }, [type, iconState]);

  const disableCopyPaste = useCallback(
    (e: any) => {
      if (type === 'password') e.preventDefault();
      return undefined;
    },
    [type],
  );

  const handleClearIconClick = (e: any) => {
    onClear && onClear();
    onBlur && onBlur(e);
    onChange && onChange(e);
    changeInputValue('');
  };

  const isEditable = useMemo(() => {
    if (!(props.disabled || props.readOnly)) {
      return true;
    }
    return false;
  }, [props.disabled, props.readOnly]);

  return (
    <InputWrapper className={className}>
      <Flex justifyContent="center" width="100%">
        {prefix && <Prefix>{prefix}</Prefix>}
        <StyledInput
          maxLength={maxLength}
          type={inputType}
          spellCheck="false"
          placeholder={placeholderText}
          ref={ref}
          value={parser ? parser(value) : value}
          onChange={(e) => {
            changeInputValue(e.target.value);
            onChange && onChange(e);
          }}
          onBlur={(e) => onBlur && onBlur(e)}
          onFocus={(e) => onFocus && onFocus(e)}
          hasIcon={Icon !== undefined || sufix !== undefined || showClearIcon}
          hasError={showError && isEditable}
          className={
            validationError && validators ? 'form-input-not-valid' : ''
          }
          onKeyDown={handleKeyDown}
          onCopy={disableCopyPaste}
          onPaste={disableCopyPaste}
          prefix={prefix}
          hasPrefix={!!prefix}
          {...props}
        />
        <IconsWrapper>
          {sufix && <Sufix>{sufix}</Sufix>}
          {showClearIcon && isEditable && (
            <IconContainer onClick={handleClearIconClick}>
              <StyledClearIcon />
            </IconContainer>
          )}
          {Icon && (
            <IconContainer
              onClick={handleIconClick}
              disabled={props.disabled}
              readOnly={props.readOnly}>
              {Icon}
            </IconContainer>
          )}
        </IconsWrapper>
      </Flex>

      <InputError hasError={showError && isEditable}>
        <p>{lastErrorText}</p>
      </InputError>
    </InputWrapper>
  );
};
